"use client";

import { safeT } from "@/utils/safeT";
import { Delete, DeleteOutline, RestoreFromTrash } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";

type DeleteToggleButtonProps = {
  id: string;
  lang: string;
  initialDelete?: boolean;
  fontSize?: number;
  onToggle?: (id: string, lang: string, hidden: boolean) => void;
};

export default function DeleteToggleButton({
  id,
  lang,
  initialDelete = true,
  fontSize = 20,
  onToggle,
}: DeleteToggleButtonProps) {
  const [del, setDel] = useState(initialDelete);
  const [lastClicked, setLastClicked] = useState(0);
  const t = useTranslations();

  const handleToggle = useCallback(() => {
    // 용도	디바운스 시간
    // 버튼 클릭 제한 (중복 클릭 방지)	✅ 1000~1500ms (1~1.5초)
    // 검색창 자동완성	300~500ms
    // 윈도우 resize, scroll 이벤트	200~300ms

    const now = Date.now();
    if (now - lastClicked < 1500) return; // 1.5초 디바운스
    setLastClicked(now);

    const newDel = !del;
    setDel(newDel);
    if (onToggle) onToggle(id, lang, del);
  }, [del, lastClicked, id, onToggle]);

  return (
    <IconButton
      aria-label={
        del
          ? safeT(t, "ui.label.delete", {
              defaultMessage: "홈으로 안전 귀환",
            })
          : safeT(t, "ui.label.undoDelete", {
              defaultMessage: "삭제 취소",
            })
      }
      size="small"
      onClick={handleToggle}
      sx={{
        borderRadius: "8px", // 둥근 정도 조절 (4~8 추천)
        px: 0.8, // 가로 패딩 줘야 너비 확보
        py: 0.5, // 세로 패딩은 작게
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        {del ? (
          <DeleteOutline sx={{ fontSize, color: "text.secondary" }} />
        ) : (
          <RestoreFromTrash sx={{ fontSize, color: "text.secondary" }} />
        )}
        <Typography variant="body2">
          {del
            ? ""
            : safeT(t, "ui.label.undoDelete", {
                defaultMessage: "삭제 취소",
              })}
        </Typography>
      </Stack>
    </IconButton>
  );
}
